"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  ForceAreaPanel,
  HydrostaticPanel,
} from "@/components/tools/physics-fields/pressure-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const PRESSURE_CALCULATOR_STRINGS = {
  modeLabel: "What kind of pressure?",
  modeForceArea: "Force on an area: P = F/A",
  modeFluid: "Depth in a fluid: P = ρgh",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  forceAreaExamples: {
    person: "A 70 kg person on both feet (400 cm²)",
    heel: "The same person on one stiletto heel (1 cm²)",
    tack: "A thumbtack pushed with 10 N (0.1 mm² tip)",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  pressure: "Pressure P",
  pressureShort: "Pressure",
  force: "Force F (perpendicular to the surface)",
  forceShort: "Force",
  area: "Area A",
  areaShort: "Area",
  fluidLabel: "Fluid",
  planetLabel: "Gravity of",
  fluidNames: {
    "fresh-water": "Fresh water (20 °C)",
    seawater: "Seawater",
    mercury: "Mercury (20 °C)",
    ethanol: "Ethanol (20 °C)",
    glycerol: "Glycerol (20 °C)",
    air: "Air (sea level, standard atmosphere)",
  },
  planetNames: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth (standard gravity)",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  depthShort: "Depth",
  densityShort: "Density",
  basisLabel: "The pressure I enter or want is",
  gauge: "Gauge (above the atmosphere)",
  absolute: "Absolute (above a vacuum)",
  gaugePressure: "Gauge pressure ρgh",
  absolutePressure: "Absolute pressure P₀ + ρgh",
  density: "Density ρ",
  gravity: "Gravity g",
  depth: "Depth h",
  depthHint: "Measured down from the surface of the fluid.",
  surface: "Pressure at the surface P₀",
  surfaceHint:
    "Standard atmosphere is 101325 Pa. Use 0 for a fluid in a vacuum.",
  inAtmospheres: "In standard atmospheres",
  absoluteInAtmospheres: "Absolute, in atmospheres",
  depthPerAtmosphere: "Depth for each extra atmosphere",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  working: "The working",
  forceAreaLabel: "A force spread over an area",
  forceAreaCaption:
    "The same force gives a larger pressure on a smaller area — the idea behind a knife edge and a snowshoe.",
  forceAreaDescription:
    "A block pressed down by a force arrow F onto a flat surface, with the contact area A marked below it and short arrows showing the pressure on the surface.",
  fluidDiagramLabel: "Pressure in a column of fluid",
  fluidCaption:
    "Pressure grows in proportion to the depth h, plus the pressure P₀ pressing on the surface.",
  fluidDescription:
    "A tank of fluid with arrows labelled P0 pressing down on its surface, a vertical double arrow marking the depth h, and arrows at the bottom labelled rho g h.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 1.2e5 works.",
  errorPositive:
    "Force, area, density and gravity must be greater than zero, and depth and surface pressure cannot be negative.",
  errorBelowAtmosphere:
    "That pressure is below the surface pressure, which would put the point above the fluid. Enter a larger pressure, or switch to gauge pressure.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes the force is perpendicular to the surface and spread evenly over it. In a fluid it assumes the fluid is at rest, incompressible and of uniform density, with the same g throughout. Real water gets about 5% denser at the bottom of the deepest oceans, and gases are far more compressible. Unit conversion lives in the pressure unit converter.",
} as const;

type Mode = "force-area" | "fluid";

export default function PressureCalculatorWidget() {
  const strings = useToolUi(PRESSURE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("force-area");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pc-mode"
        onChange={setMode}
        options={[
          { value: "force-area", label: strings.modeForceArea },
          { value: "fluid", label: strings.modeFluid },
        ]}
        value={mode}
      />
      {mode === "force-area" ? (
        <ForceAreaPanel sig={sig} strings={strings} />
      ) : (
        <HydrostaticPanel sig={sig} strings={strings} />
      )}
      <SigFigsField
        format={fmt}
        id="pc-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
